import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Mail, RotateCcw, Trash2 } from "lucide-react";
import {
  BilingualField,
  Panel,
  Repeater,
  TextField,
  Toggle,
} from "@/components/studio/Fields";
import { deleteMessage, ownerMessages, setMessageHandled } from "@/lib/site";

/* Two halves: the answers you publish, and the questions you did not anticipate.
   They belong together — an unread message that keeps recurring is a FAQ entry
   waiting to be written. */

const Inbox = () => {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = () =>
    ownerMessages()
      .then((data) => {
        setMessages(data.messages || []);
        setUnread(data.unread || 0);
      })
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);

  const toggle = async (message) => {
    await setMessageHandled(message.id, !message.handled);
    refresh();
  };

  const remove = async (message) => {
    if (!window.confirm("Supprimer ce message ? C’est définitif.")) return;
    await deleteMessage(message.id);
    toast.success("Message supprimé");
    refresh();
  };

  return (
    <Panel
      title="Messages reçus"
      hint="Le formulaire d’aide dépose ici. Rien n’est envoyé par courriel : c’est à vous de venir voir, et c’est pour ça que la page affiche aussi votre adresse."
      actions={
        unread ? (
          <span
            className="font-num rounded-full px-2.5 py-1 text-[12px] font-semibold"
            style={{ backgroundColor: "var(--paper-2)", color: "var(--warning)" }}
            data-testid="unread-count"
          >
            {unread} non lu{unread > 1 ? "s" : ""}
          </span>
        ) : null
      }
    >
      {loading ? (
        <p className="flex items-center gap-2 text-[13px] text-[color:var(--muted-ink)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
        </p>
      ) : messages.length ? (
        <ul className="space-y-3" data-testid="message-list">
          {messages.map((message) => (
            <li
              key={message.id}
              className="rounded-lg border p-4"
              style={{
                borderColor: "var(--line)",
                backgroundColor: message.handled ? "transparent" : "var(--paper)",
              }}
              data-testid={`message-${message.id}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[14px] font-medium">
                    {message.subject || "(sans sujet)"}
                  </p>
                  <p className="font-num mt-0.5 text-[11px] text-[color:var(--muted-ink)]">
                    {[
                      message.name || "anonyme",
                      message.lang?.toUpperCase(),
                      (message.created_at || "").slice(0, 16).replace("T", " "),
                      message.page,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {message.email ? (
                    <a
                      href={`mailto:${message.email}?subject=${encodeURIComponent(
                        `Re: ${message.subject || "votre message"}`
                      )}`}
                      className="rounded-full border p-2"
                      style={{ borderColor: "var(--line)" }}
                      title={`Répondre à ${message.email}`}
                      data-testid={`reply-${message.id}`}
                    >
                      <Mail className="h-3.5 w-3.5" style={{ color: "var(--ink-soft)" }} />
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => toggle(message)}
                    className="rounded-full border p-2"
                    style={{ borderColor: "var(--line)" }}
                    title={message.handled ? "Marquer non traité" : "Marquer traité"}
                    data-testid={`handled-${message.id}`}
                  >
                    {message.handled ? (
                      <RotateCcw className="h-3.5 w-3.5" style={{ color: "var(--muted-ink)" }} />
                    ) : (
                      <Check className="h-3.5 w-3.5" style={{ color: "var(--success)" }} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(message)}
                    className="rounded-full border p-2"
                    style={{ borderColor: "var(--line)" }}
                    title="Supprimer"
                  >
                    <Trash2 className="h-3.5 w-3.5" style={{ color: "var(--danger)" }} />
                  </button>
                </div>
              </div>
              <p className="mt-3 whitespace-pre-line text-[13.5px] leading-relaxed text-[color:var(--ink-soft)]">
                {message.message}
              </p>
              {message.email ? (
                <p className="font-num mt-2 text-[11px] text-[color:var(--muted-ink)]">
                  {message.email}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-[color:var(--ink-soft)]">Aucun message.</p>
      )}
    </Panel>
  );
};

export const SupportPanel = ({ draft, set }) => {
  const support = draft.support || {};

  return (
    <div className="space-y-6">
      <Panel
        title="Page d’aide"
        hint="Écrite, publique, indexée par Google — contrairement à une conversation. C’est aussi ce socle que le futur agent conversationnel aura le droit de réciter, et rien d’autre."
      >
        <BilingualField
          label="Titre"
          value={support.title}
          onChange={(value) => set("support.title", value)}
          testId="support-title"
        />
        <BilingualField
          label="Introduction"
          value={support.intro}
          onChange={(value) => set("support.intro", value)}
          rows={2}
          testId="support-intro"
        />
        <TextField
          label="Adresse de contact"
          hint="Affichée sur la page, à côté du formulaire. Laissez vide pour n’afficher que le formulaire."
          value={support.contact_email}
          onChange={(value) => set("support.contact_email", value)}
          placeholder="bonjour@placy.events"
          testId="support-email"
        />
        <BilingualField
          label="Phrase au-dessus du formulaire"
          value={support.contact_intro}
          onChange={(value) => set("support.contact_intro", value)}
          rows={2}
          testId="support-contact-intro"
        />
      </Panel>

      <Panel
        title="Questions fréquentes"
        hint="Une réponse concrète vaut mieux que trois évasives. Écrivez comme vous répondriez au téléphone."
      >
        <Repeater
          items={support.faq || []}
          onChange={(next) => set("support.faq", next)}
          blank={() => ({
            question: { fr: "", en: "" },
            answer: { fr: "", en: "" },
            visible: true,
          })}
          addLabel="Ajouter une question"
          testId="faq-entries"
          renderItem={(item, patch) => (
            <>
              <BilingualField
                label="Question"
                value={item.question}
                onChange={(value) => patch({ question: value })}
                testId={`faq-q-${item.id || "new"}`}
              />
              <BilingualField
                label="Réponse"
                value={item.answer}
                onChange={(value) => patch({ answer: value })}
                rows={5}
                testId={`faq-a-${item.id || "new"}`}
              />
              <Toggle
                label="Visible sur le site"
                checked={item.visible !== false}
                onChange={(value) => patch({ visible: value })}
                testId={`faq-visible-${item.id || "new"}`}
              />
            </>
          )}
        />
      </Panel>

      <Inbox />
    </div>
  );
};

export default SupportPanel;
